import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Grid,
  CssBaseline,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import axios from "axios";
import LeaveRequestForm from "./LeaveRequestForm";
import PermissionRequestForm from "./PermissionRequestForm";

// RTL theme and cache
const cacheRtl = createCache({ key: "muirtl", prepend: true });
const theme = createTheme({
  direction: "rtl",
  palette: {
    mode: "dark",
    background: { default: "#121212", paper: "#1E1E1E" },
    text: { primary: "#FFFFFF", secondary: "#BBBBBB" },
    primary: { main: "#6fd943" },
    error: { main: "#f44336" },
  },
  typography: { fontFamily: "Cairo, sans-serif" },
});

const PermissionsAndLeaves = () => {
  const [permissions, setPermissions] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const location = useLocation();
  const username = location.state?.username;

  // Fetch permissions
  useEffect(() => {
    if (username) {
      fetch(
        `https://backenderpforseg.samielmetwali.com/getpermissions.php/?username=${username}`
      )
        .then((res) => res.json())
        .then((data) => Array.isArray(data) && setPermissions(data))
        .catch(() => setPermissions([]));
    }
  }, [username]);

  // Fetch leaves
  useEffect(() => {
    if (username) {
      axios
        .get(
          `https://backenderpforseg.samielmetwali.com/leaveRequest.php?username=${username}`
        )
        .then((res) => setLeaves(res.data))
        .catch(() => setLeaves([]));
    }
  }, [username]);

  const handleLeaveSubmit = async (newLeave) => {
    try {
      const res = await axios.post(
        "https://backenderpforseg.samielmetwali.com/postLeaveRequest.php",
        { ...newLeave, username }
      );

      if (res.data.status === "success") {
        // إعادة تحميل الإجازات بعد الإضافة
        const updatedLeaves = await axios.get(
          `https://backenderpforseg.samielmetwali.com/leaveRequest.php?username=${username}`
        );
        setLeaves(updatedLeaves.data);
      }
    } catch (err) {
      console.error("Failed to submit leave request:", err);
    }
  };

  const handlePermissionSubmit = async (newPermission) => {
    try {
      const response = await fetch(
        "https://backenderpforseg.samielmetwali.com/postpermission.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...newPermission, username }),
        }
      );

      const result = await response.json();
      if (result.status === "success") {
        const updated = await fetch(
          `https://backenderpforseg.samielmetwali.com/getpermissions.php/?username=${username}`
        );
        const data = await updated.json();
        if (Array.isArray(data)) setPermissions(data);
      }
    } catch (err) {
      console.error("Failed to submit permission:", err);
    }
  };

  const getStatusStyle = (status) => {
    if (status === 1) return { backgroundColor: "#2E7D32", color: "#fff" };
    if (status === 0) return { backgroundColor: "#C62828", color: "#fff" };
    return {};
  };

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          p={2}
          style={{ backgroundColor: theme.palette.background.default }}
        >
          <Typography variant="h5" align="center">
            الأذونات والإجازات
          </Typography>
          <Typography align="center" color="error" fontSize={14}>
            لا يمكنك طلب اكثر من 2 يوم اجازه فالشهر
          </Typography>
          <Typography align="center" color="error" fontSize={14}>
            لا يمكنك طلب أكثر من 5 ساعات إذن فالشهر
          </Typography>

          <Grid container spacing={4} mt={2}>
            {/* Permissions */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" align="right">
                  الأذونات السابقة
                </Typography>
                <List>
                  {permissions.map((p, i) => (
                    <ListItem key={i} sx={getStatusStyle(p.is_accepted)}>
                      <ListItemText
                        primary={p.permission_type}
                        secondary={`${p.start_time} - ${p.end_time} | ${
                          p.is_accepted === 1
                            ? "تم القبول"
                            : p.is_accepted === 0
                            ? "تم الرفض"
                            : "قيد المراجعة"
                        } | ${p.date || "غير متاح"}`}
                        sx={{ textAlign: "right" }}
                      />
                    </ListItem>
                  ))}
                </List>
                {/* <PermissionRequestForm onSubmit={handlePermissionSubmit} /> */}
                <PermissionRequestForm
                  onSubmit={handlePermissionSubmit}
                  onSuccess={() => {
                    if (username) {
                      fetch(
                        `https://backenderpforseg.samielmetwali.com/getpermissions.php/?username=${username}`
                      )
                        .then((res) => res.json())
                        .then(
                          (data) => Array.isArray(data) && setPermissions(data)
                        )
                        .catch(() => setPermissions([]));
                    }
                  }}
                />
              </Paper>
            </Grid>

            {/* Leaves */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" align="right">
                  الإجازات السابقة
                </Typography>
                <List>
                  {leaves.map((l, i) => (
                    <ListItem key={i} sx={getStatusStyle(l.is_accepted)}>
                      <ListItemText
                        primary={l.leave_type}
                        secondary={`${l.start_date} - ${l.end_date} | ${
                          l.is_accepted === 1
                            ? "تم القبول"
                            : l.is_accepted === 0
                            ? "تم الرفض"
                            : "قيد المراجعة"
                        } | ${l.created_at || "غير متاح"}`}
                        sx={{ textAlign: "right" }}
                      />
                    </ListItem>
                  ))}
                </List>
                <LeaveRequestForm
                  username={username}
                  onSubmit={handleLeaveSubmit}
                  isAdmin={false}
                  onSuccess={() => {
                    if (username) {
                      axios
                        .get(
                          `https://backenderpforseg.samielmetwali.com/leaveRequest.php?username=${username}`
                        )
                        .then((res) => setLeaves(res.data))
                        .catch(() => setLeaves([]));
                    }
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default PermissionsAndLeaves;
